import React, { useRef, useState } from 'react';
import { useTheme } from '../theming/default_theme';
import axios from 'axios';
import { post } from '../api/apiService';
import { apiEndPoints } from '../api/apiEndPoints';
import messageBundle from '../assets/constants/message_bundle';
import * as XLSX from 'xlsx';
import { isTodayOrPastDate, isValidDDMMYYYY } from '../utils/dateUtils';

const SpectacleFileUploadModel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isFileHasError, setIsFileHasError] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    function getCaseInsensitive(row: any, key: string) {
        const foundKey = Object.keys(row).find(k => k.trim().toLowerCase() === key.trim().toLowerCase());
        return foundKey ? row[foundKey] : undefined;
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setError('');
        setIsFileHasError(false);

        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const data = e.target?.result;
                let workbook;
                if (data instanceof ArrayBuffer) {
                    const binaryString = new Uint8Array(data)
                        .reduce((acc, byte) => acc + String.fromCharCode(byte), '');
                    workbook = XLSX.read(binaryString, { type: 'binary' });
                } else {
                    workbook = XLSX.read(data, { type: 'array' });
                }
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

                let localFileHasError = false;

                const spectacleRequests = jsonData
                    .slice(1) // skip header row if needed
                    .map((row: any, index: number) => {
                        const spectaclePayload: any = {};
                        const rightEye: any = {};
                        const leftEye: any = {};

                        function requiredField(fieldValue: any, fieldName: string) {
                            if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
                                setError(`${fieldName} is required.`);
                                localFileHasError = true;
                                return false;
                            }
                            return true;
                        }
                        const hospitalName = getCaseInsensitive(row, 'Hospital Name');
                        if (!requiredField(hospitalName, 'Hospital Name')) return null;
                        spectaclePayload.hospitalName = String(hospitalName).trim();

                        const hospitalCode = getCaseInsensitive(row, 'Hospital Code');
                        if (!requiredField(hospitalCode, 'Hospital Code')) return null;
                        spectaclePayload.hospitalCode = String(hospitalCode).trim();

                        const location = getCaseInsensitive(row, 'Location');
                        if (!requiredField(location, 'Hospital Location')) return null;
                        spectaclePayload.location = String(location).trim();

                        const state = getCaseInsensitive(row, 'State');
                        if (!requiredField(state, 'Hospital State')) return null;
                        spectaclePayload.state = String(state).trim();

                        const city = getCaseInsensitive(row, 'City');
                        if (!requiredField(city, 'Hospital City')) return null;
                        spectaclePayload.city = String(city).trim();

                        const pincode = getCaseInsensitive(row, 'Pincode');
                        if (!requiredField(pincode, 'Hospital Pincode')) return null;
                        spectaclePayload.pincode = String(pincode).trim();

                        const referrals = getCaseInsensitive(row, 'Referrals');
                        if (!requiredField(referrals, 'Hospital Referrals')) return null;
                        spectaclePayload.referrals = String(referrals).trim();

                        const guardianContact = getCaseInsensitive(row, 'Guardian Contact');
                        if (!requiredField(guardianContact, 'Guardian Contact')) return null;
                        spectaclePayload.guardianContact = String(guardianContact).trim();

                        const age = getCaseInsensitive(row, 'Age');
                        if (!requiredField(age, 'Age')) return null;
                        spectaclePayload.age = String(age).trim();

                        const gender = getCaseInsensitive(row, 'Gender');
                        if (!requiredField(gender, 'Gender')) return null;
                        spectaclePayload.gender = String(gender).trim();

                        // const RequestedOn = getCaseInsensitive(row, 'Requested On');
                        // if (!requiredField(RequestedOn, 'Requested On')) return null;
                        // spectaclePayload.RequestedOn = String(RequestedOn).trim(); // treat as text

                        const requestedOn = getCaseInsensitive(row, 'Requested On');
                        if (!requiredField(requestedOn, 'Requested On'))
                            return;
                        if (requestedOn) {
                            // console.log("hospitalRegistrationDate-->", hospitalRegistrationDate);

                            let regOnDateStr = String(requestedOn).trim();

                            if (!/^\d{2}-\d{2}-\d{4}$/.test(regOnDateStr)) {
                                if (!isNaN(Number(requestedOn)) && requestedOn !== '') {
                                    regOnDateStr = excelSerialToDateString(Number(requestedOn) + 1);
                                }
                                if (!isValidDDMMYYYY(regOnDateStr) || !/^\d{2}-\d{2}-\d{4}$/.test(regOnDateStr)) {
                                    setError('Requested On must be a valid date in DD-MM-YYYY format. Found: ' + regOnDateStr);
                                    localFileHasError = true;
                                    return null;
                                }
                                else if (!isTodayOrPastDate(regOnDateStr)) {
                                    setError('Requested On cannot be future date. Found: ' + regOnDateStr);
                                    localFileHasError = true;
                                    return null;

                                }


                            } else if (regOnDateStr.length === 10) {
                                // If it's a Date object, format it to DD-MM-YYYY
                                if (requestedOn instanceof Date && !isNaN(requestedOn.getTime())) {
                                    const d = requestedOn;
                                    regOnDateStr = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
                                }
                                // If it's an Excel serial number, convert to date
                                else if (!isNaN(Number(requestedOn)) && (!/^\d{2}-\d{2}-\d{4}$/.test(regOnDateStr)) && requestedOn !== '') {
                                    // Excel's epoch starts at 1900-01-01
                                    const excelEpoch = new Date(1899, 11, 30);
                                    const days = Number(requestedOn);
                                    const d = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);
                                    regOnDateStr = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
                                    console.log("you are insided here-->", regOnDateStr);
                                }

                                if (!isValidDDMMYYYY(regOnDateStr) || !/^\d{2}-\d{2}-\d{4}$/.test(regOnDateStr)) {
                                    setError('Requested On must be a valid date in DD-MM-YYYY format. Found: ' + regOnDateStr);
                                    localFileHasError = true;
                                    return null;
                                }
                            }
                            console.log("regOnDateStr-->", regOnDateStr);
                            spectaclePayload.registration_date = regOnDateStr;
                        } else {
                            spectaclePayload.registration_date = '';
                        }

                        // Right Eye
                        const rightSph = getCaseInsensitive(row, 'Right');
                        if (!requiredField(rightSph, 'Right Sph')) return null;
                        rightEye.sph = String(rightSph).trim();

                        const rightCyl = getCaseInsensitive(row, 'Right_1');
                        if (!requiredField(rightCyl, 'Right Cyl')) return null;
                        rightEye.cyl = String(rightCyl).trim();

                        const rightAxis = getCaseInsensitive(row, 'Right_2');
                        if (!requiredField(rightAxis, 'Right Axis')) return null;
                        rightEye.axis = String(rightAxis).trim();

                        // Left Eye
                        const leftSph = getCaseInsensitive(row, 'Left');
                        if (!requiredField(leftSph, 'Left Sph')) return null;
                        leftEye.sph = String(leftSph).trim();

                        const leftCyl = getCaseInsensitive(row, 'Left_1');
                        if (!requiredField(leftCyl, 'Left Cyl')) return null;
                        leftEye.cyl = String(leftCyl).trim();

                        const leftAxis = getCaseInsensitive(row, 'Left_2');
                        if (!requiredField(leftAxis, 'Left Axis')) return null;
                        leftEye.axis = String(leftAxis).trim();

                        spectaclePayload.rightEye = rightEye;
                        spectaclePayload.leftEye = leftEye;

                        const status = getCaseInsensitive(row, 'Status');
                        if (!requiredField(status, 'Status')) return null;
                        spectaclePayload.status = String(status).trim().toUpperCase();


                        return spectaclePayload;
                    })
                    .filter(Boolean);

                const spectaclesRecords = new Map<string, number>();
                const duplicateIndex = spectacleRequests.findIndex((req, index) => {
                    const key = `${req.hospitalCode}-${req.guardianContact}-${req.age}-${req.gender}-${req.referrals}`.toLowerCase();
                    if (spectaclesRecords.has(key)) {
                        return true;
                    } else {
                        spectaclesRecords.set(key, index);
                        return false;
                    }
                });

                if (duplicateIndex !== -1) {
                    setError(`Duplicate referral found with the same hospital ID found at row ${duplicateIndex + 3}. Please check and try again`)
                    localFileHasError = true;
                    return;
                }
                setIsFileHasError(localFileHasError);

                if (!localFileHasError && spectacleRequests.length > 0) {
                    postSpectacleRequestData(spectacleRequests);
                }
            };

            reader.readAsArrayBuffer(file);
        }
    };


    function handleButtonClick() {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }

    const postSpectacleRequestData = async (spectacleData: any[]) => {

        if (!spectacleData.length) {
            setError('No data to send');

        }
        else {
            setLoading(true);
            try {
                const url = apiEndPoints.spectacleRequestsURL;
                console.log('the url is ', url)// Replace with your API endpoint
                const response = await post(url, // Replace with your API endpoint
                    spectacleData, // Send parsed data as payload
                ); // Replace with your API endpoint
                console.log('API Response:', response);
                setSuccessMessage(`File uploaded successfully!\n 
                    Total Records: ${response.totalRecords}, Updated Records: ${response.updatedRecords}, Rejected Records: ${response.rejectedRecords} `);
                setTimeout(() => {
                    setSuccessMessage('');
                    onClose();
                }, 5000);

                // if (response && response.includes('Successfully uploaded')) {

                //   setError(response);

                // }
                // else if (response && response.includes('No new hospitals to save')) {

                //   setError('No new hospitals to save');
                // }


            } catch (err) {
                if (err instanceof Error) {
                    setError('Failed to send data: ' + err.message);

                } else {
                    console.log('Unknown error', err);
                }

            }
            setLoading(false);
            return;
        }
    }

    function excelSerialToDateString(serial: number): string {
        // Excel's leap year bug: 1900 is not a leap year, but Excel treats it as one
        if (serial >= 60) serial -= 1;
        const excelEpoch = Date.UTC(1899, 11, 30); // 1899-12-30 in UTC
        const ms = excelEpoch + serial * 24 * 60 * 60 * 1000;
        const d = new Date(ms);
        const day = String(d.getUTCDate()).padStart(2, '0');
        const month = String(d.getUTCMonth() + 1).padStart(2, '0');
        const year = d.getUTCFullYear();
        return `${day}-${month}-${year}`;
    }

    return (
        <div className="flex flex-col justify-center items-center fixed top-0 left-0 w-screen h-screen bg-black bg-opacity-50 z-50">
            <div className="flex flex-col bg-white rounded-xl shadow-lg w-1/2 h-1/2 relative ">
                <div className={`flex flex-col block justify-center items-center h-[40%] rounded-xl relative`} style={{ background: 'linear-gradient(to right, #F9F7ED, #FFFFF5, #F5F1E0)' }}>
                    <div className="absolute top-4 right-4 flex items-center justify-center w-[3%] h-[15%] cursor-pointer" onClick={onClose}>
                        <img src="/close.png" alt="Close" />
                    </div>
                    <div className="flex flex-col block justify-center items-center gap-y-5">
                        <span style={{ fontFamily: theme.typography.fontFamily, fontSize: theme.typography.h1.fontSize, fontWeight: theme.typography.fontWeightBold }}>{messageBundle.fileUploadModal.textMessages.uploadSpectacleRequestMessage}</span>
                        <span style={{ fontFamily: theme.typography.fontFamily, fontSize: theme.typography.fontSize, fontWeight: theme.typography.fontWeightMedium, color: theme.colors.detailtext }}>{messageBundle.fileUploadModal.textMessages.uploadSpectacleRequestImportMessage}</span>
                        <img src="/file.png" alt="File Icon" className="absolute right-9 top-3/4 -translate-y-1 w-[20%] h-[80%]" />
                    </div>
                </div>
                <div className="flex flex-col block justify-center items-center h-[60%] bg-white rounded-b-[20px]">
                    <span>{messageBundle.fileUploadModal.textMessages.uploadHospitalDataMsg3} </span>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                    <button className={`flex bg-[${theme.colors.surface}] text-white rounded-full h-[15%] p-4 pl-12 pr-12 items-center justify-center mt-10`} onClick={handleButtonClick}>{messageBundle.fileUploadModal.labels.upLoadCSV}</button>
                    {/* Error message display */}
                    {error && (
                        <div className="mt-4 text-red-600 text-xs font-semibold text-center">
                            {error}
                        </div>
                    )}
                </div>
            </div>
            {successMessage && (
                <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded shadow-lg z-[9999] text-sm font-semibold animate-fade-in">
                    {successMessage}
                </div>
            )}
        </div>
    );
};

export default SpectacleFileUploadModel;